import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { Navigation } from "@/pages/Index/components/Navigation";
import { Footer } from "@/pages/Index/components/Footer";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import { Calendar, Clock, Users, Euro, MapPin, Mail } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const META: Record<string, { title: string; description: string }> = {
  es: {
    title: "Etapas educativas | Gándara International School",
    description: "Infantil (3-6 años) y Primaria (6-12 años) con pedagogía Montessori. Ratios pequeñas y aprendizaje trilingüe en Gondomar.",
  },
  en: {
    title: "Educational Stages | Gándara International School",
    description: "Kindergarten (3-6) and Elementary (6-12) with Montessori pedagogy. Small ratios and trilingual learning in Gondomar.",
  },
  pt: {
    title: "Etapas Educativas | Gándara International School",
    description: "Infantil (3-6 anos) e Primária (6-12 anos) com pedagogia Montessori. Rácios pequenos e aprendizagem trilingue em Gondomar.",
  },
  fr: {
    title: "Étapes Éducatives | Gándara International School",
    description: "Maternelle (3-6 ans) et Primaire (6-12 ans) avec pédagogie Montessori. Petits effectifs et apprentissage trilingue à Gondomar.",
  },
};

const Courses = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const meta = META[language] || META.es;
  usePageMeta({ title: meta.title, description: meta.description });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner />
      
      <div className="container mx-auto px-4 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-6xl mx-auto"
        >
          <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-light text-primary mb-6">
            {t.courses.title}
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {t.courses.subtitle}
          </p>
        </div>

        {/* Course 1: Acompañamiento */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16"
        >
          <div className="bg-card rounded-2xl p-8 shadow-lg border border-border">
            <h2 className="text-3xl font-bold text-primary mb-4">
              {t.courses.accompaniment.title}
            </h2>
            <h3 className="text-xl font-semibold text-secondary mb-6">
              {t.courses.accompaniment.subtitle}
            </h3>
            
            <p className="text-muted-foreground mb-8 leading-relaxed">
              {t.courses.accompaniment.description}
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Trainers */}
              <div>
                <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {t.courses.accompaniment.trainers.title}
                </h4>
                <p className="text-muted-foreground">{t.courses.accompaniment.trainers.names}</p>
              </div>

              {/* Duration */}
              <div>
                <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {t.courses.accompaniment.duration.title}
                </h4>
                <ul className="text-muted-foreground space-y-1">
                  <li>{t.courses.accompaniment.duration.block1}</li>
                  <li>{t.courses.accompaniment.duration.block2}</li>
                  <li>{t.courses.accompaniment.duration.block3}</li>
                </ul>
              </div>

              {/* Dates */}
              <div>
                <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {t.courses.accompaniment.dates.title}
                </h4>
                <ul className="text-muted-foreground space-y-1">
                  <li><strong>{t.courses.accompaniment.dates.block1}:</strong> {t.courses.accompaniment.dates.block1Dates}</li>
                  <li><strong>{t.courses.accompaniment.dates.block2}:</strong> {t.courses.accompaniment.dates.block2Dates}</li>
                  <li><strong>{t.courses.accompaniment.dates.block3}:</strong> {t.courses.accompaniment.dates.block3Dates}</li>
                </ul>
              </div>

              {/* Schedule */}
              <div>
                <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {t.courses.accompaniment.schedule.title}
                </h4>
                <ul className="text-muted-foreground space-y-1">
                  <li><strong>{t.courses.accompaniment.schedule.friday}:</strong> {t.courses.accompaniment.schedule.fridayTime}</li>
                  <li><strong>{t.courses.accompaniment.schedule.saturday}:</strong> {t.courses.accompaniment.schedule.saturdayTime}</li>
                </ul>
              </div>

              {/* Location */}
              <div>
                <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  {t.courses.accompaniment.location.title}
                </h4>
                <p className="text-muted-foreground">{t.courses.accompaniment.location.place}</p>
              </div>

              {/* Capacity */}
              <div>
                <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {t.courses.accompaniment.capacity.title}
                </h4>
                <ul className="text-muted-foreground space-y-1">
                  <li>{t.courses.accompaniment.capacity.block1}</li>
                  <li>{t.courses.accompaniment.capacity.blocks23}</li>
                </ul>
              </div>
            </div>

            {/* Prices */}
            <div className="bg-primary/5 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                <Euro className="w-5 h-5" />
                {t.courses.accompaniment.prices.title}
              </h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>• {t.courses.accompaniment.prices.fullEarly}</li>
                <li>• {t.courses.accompaniment.prices.fullBlocks}</li>
                <li>• {t.courses.accompaniment.prices.block12}</li>
                <li>• {t.courses.accompaniment.prices.block3}</li>
              </ul>
              <div className="mt-4 space-y-2 text-sm text-muted-foreground/70">
                <p>* {t.courses.accompaniment.prices.note1}</p>
                <p>* {t.courses.accompaniment.prices.note2}</p>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
              <h4 className="text-xl font-semibold text-primary">
                {t.courses.accompaniment.content.title}
              </h4>
              
              <div className="space-y-8">
                {/* Block 1 & 2 */}
                <div>
                  <h5 className="text-lg font-semibold text-secondary mb-3">
                    {t.courses.accompaniment.content.blocks12.title}
                  </h5>
                  <p className="text-muted-foreground mb-4">{t.courses.accompaniment.content.blocks12.instructor}</p>
                  
                  <div className="space-y-4">
                    <div>
                      <h6 className="font-semibold text-primary">{t.courses.accompaniment.content.blocks12.block1.title}</h6>
                      <h6 className="text-sm text-muted-foreground/70 mb-2">{t.courses.accompaniment.content.blocks12.block1.topic}</h6>
                      <ul className="text-muted-foreground space-y-1 ml-4">
                        <li>• {t.courses.accompaniment.content.blocks12.block1.point1}</li>
                        <li>• {t.courses.accompaniment.content.blocks12.block1.point2}</li>
                        <li>• {t.courses.accompaniment.content.blocks12.block1.point3}</li>
                        <li>• {t.courses.accompaniment.content.blocks12.block1.point4}</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h6 className="font-semibold text-primary">{t.courses.accompaniment.content.blocks12.weekend2.title}</h6>
                      <h6 className="text-sm text-muted-foreground/70 mb-2">{t.courses.accompaniment.content.blocks12.weekend2.topic}</h6>
                      <ul className="text-muted-foreground space-y-1 ml-4">
                        <li>• {t.courses.accompaniment.content.blocks12.weekend2.point1}</li>
                        <li>• {t.courses.accompaniment.content.blocks12.weekend2.point2}</li>
                        <li>• {t.courses.accompaniment.content.blocks12.weekend2.point3}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Block 2 continued */}
                <div>
                  <h5 className="text-lg font-semibold text-secondary mb-3">
                    {t.courses.accompaniment.content.block2.title}
                  </h5>
                  
                  <div className="space-y-4">
                    <div>
                      <h6 className="font-semibold text-primary">{t.courses.accompaniment.content.block2.weekend3.title}</h6>
                      <h6 className="text-sm text-muted-foreground/70 mb-2">{t.courses.accompaniment.content.block2.weekend3.topic1}</h6>
                      <ul className="text-muted-foreground space-y-1 ml-4">
                        <li>• {t.courses.accompaniment.content.block2.weekend3.point1}</li>
                        <li>• {t.courses.accompaniment.content.block2.weekend3.point2}</li>
                        <li>• {t.courses.accompaniment.content.block2.weekend3.point3}</li>
                        <li>• {t.courses.accompaniment.content.block2.weekend3.point4}</li>
                      </ul>
                      
                      <h6 className="text-sm text-muted-foreground/70 mb-2 mt-4">{t.courses.accompaniment.content.block2.weekend3.topic2}</h6>
                      <ul className="text-muted-foreground space-y-1 ml-4">
                        <li>• {t.courses.accompaniment.content.block2.weekend3.point5}</li>
                        <li>• {t.courses.accompaniment.content.block2.weekend3.point6}</li>
                        <li>• {t.courses.accompaniment.content.block2.weekend3.point7}</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h6 className="font-semibold text-primary">{t.courses.accompaniment.content.block2.weekend4.title}</h6>
                      <h6 className="text-sm text-muted-foreground/70 mb-2">{t.courses.accompaniment.content.block2.weekend4.topic}</h6>
                      <ul className="text-muted-foreground space-y-1 ml-4">
                        <li>• {t.courses.accompaniment.content.block2.weekend4.point1}</li>
                        <li>• {t.courses.accompaniment.content.block2.weekend4.point2}</li>
                        <li>• {t.courses.accompaniment.content.block2.weekend4.point3}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Block 3 */}
                <div>
                  <h5 className="text-lg font-semibold text-secondary mb-3">
                    {t.courses.accompaniment.content.block3.title}
                  </h5>
                  <p className="text-muted-foreground mb-4">{t.courses.accompaniment.content.block3.instructors}</p>
                  
                  <div className="space-y-4">
                    <div>
                      <h6 className="font-semibold text-primary">{t.courses.accompaniment.content.block3.weekend5.title}</h6>
                      <h6 className="text-sm text-muted-foreground/70 mb-2">{t.courses.accompaniment.content.block3.weekend5.topic1}</h6>
                      <ul className="text-muted-foreground space-y-1 ml-4">
                        <li>• {t.courses.accompaniment.content.block3.weekend5.point1}</li>
                        <li>• {t.courses.accompaniment.content.block3.weekend5.point2}</li>
                      </ul>
                      
                      <h6 className="text-sm text-muted-foreground/70 mb-2 mt-4">{t.courses.accompaniment.content.block3.weekend5.topic2}</h6>
                      <ul className="text-muted-foreground space-y-1 ml-4">
                        <li>• {t.courses.accompaniment.content.block3.weekend5.point3}</li>
                        <li>• {t.courses.accompaniment.content.block3.weekend5.point4}</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h6 className="font-semibold text-primary">{t.courses.accompaniment.content.block3.weekend6.title}</h6>
                      <h6 className="text-sm text-muted-foreground/70 mb-2">{t.courses.accompaniment.content.block3.weekend6.topic1}</h6>
                      <ul className="text-muted-foreground space-y-1 ml-4">
                        <li>• {t.courses.accompaniment.content.block3.weekend6.point1}</li>
                        <li>• {t.courses.accompaniment.content.block3.weekend6.point2}</li>
                      </ul>
                      
                      <h6 className="text-sm text-muted-foreground/70 mb-2 mt-4">{t.courses.accompaniment.content.block3.weekend6.topic2}</h6>
                      <ul className="text-muted-foreground space-y-1 ml-4">
                        <li>• {t.courses.accompaniment.content.block3.weekend6.point3}</li>
                        <li>• {t.courses.accompaniment.content.block3.weekend6.point4}</li>
                        <li>• {t.courses.accompaniment.content.block3.weekend6.point5}</li>
                        <li>• {t.courses.accompaniment.content.block3.weekend6.point6}</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h6 className="font-semibold text-primary">{t.courses.accompaniment.content.block3.weekend7.title}</h6>
                      <h6 className="text-sm text-muted-foreground/70 mb-2">{t.courses.accompaniment.content.block3.weekend7.topic}</h6>
                      <ul className="text-muted-foreground space-y-1 ml-4">
                        <li>• {t.courses.accompaniment.content.block3.weekend7.point1}</li>
                        <li>• {t.courses.accompaniment.content.block3.weekend7.point2}</li>
                        <li>• {t.courses.accompaniment.content.block3.weekend7.point3}</li>
                        <li>• {t.courses.accompaniment.content.block3.weekend7.point4}</li>
                        <li>• {t.courses.accompaniment.content.block3.weekend7.point5}</li>
                        <li>• {t.courses.accompaniment.content.block3.weekend7.point6}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Course 2: Psychomotricity */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-16"
        >
          <div className="bg-card rounded-2xl p-8 shadow-lg border border-border">
            <h2 className="text-3xl font-bold text-primary mb-4">
              {t.courses.psychomotricity.title}
            </h2>
            <h3 className="text-xl font-semibold text-secondary mb-6">
              {t.courses.psychomotricity.subtitle}
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Date and Time */}
              <div>
                <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {t.courses.psychomotricity.dateTime.title}
                </h4>
                <p className="text-muted-foreground">{t.courses.psychomotricity.dateTime.details}</p>
              </div>

              {/* Price */}
              <div>
                <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <Euro className="w-5 h-5" />
                  {t.courses.psychomotricity.price.title}
                </h4>
                <p className="text-muted-foreground">{t.courses.psychomotricity.price.amount}</p>
              </div>
            </div>

            <div className="bg-secondary/5 rounded-lg p-6 mb-6">
              <p className="text-muted-foreground italic">
                * {t.courses.psychomotricity.lunchNote}
              </p>
            </div>

            {/* Content */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-primary mb-4">
                {t.courses.psychomotricity.content.title}
              </h4>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✏</span>
                  {t.courses.psychomotricity.content.point1}
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✏</span>
                  {t.courses.psychomotricity.content.point2}
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✏</span>
                  {t.courses.psychomotricity.content.point3}
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✏</span>
                  {t.courses.psychomotricity.content.point4}
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✏</span>
                  {t.courses.psychomotricity.content.point5}
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✏</span>
                  {t.courses.psychomotricity.content.point6}
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center"
        >
          <div className="bg-primary/5 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-primary mb-4 flex items-center justify-center gap-2">
              <Mail className="w-5 h-5" />
              {t.courses.contact.title}
            </h3>
            <p className="text-muted-foreground mb-4">{t.courses.contact.description}</p>
            <div className="bg-primary/10 p-4 rounded-lg inline-block">
              <p className="font-medium text-primary text-lg">info@gandaraschool.com</p>
            </div>
          </div>
        </motion.div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Courses;